<template>
  <tab-layout :full-width="true">
    <div slot="tabContent" class="bg-white rounded mb-4 p-3">
      <page-heading>{{ isEdit ? 'Edit' : 'Horse Movement / Horse Health Declaration' }}</page-heading>

      <form @submit.prevent class="biosecurity-form">
        <div class="row">
          <div class="col-12 col-md-6 mb-3">
            <f-text id="bio_name" label="Name of person in charge of horses being travelled" v-model="form.namePersonInCharge" :error="fieldError('name_person_in_charge')" />
          </div>
          <div class="col-12 col-md-6 mb-3">
            <f-text id="bio_address" label="Address of person in charge" v-model="form.address" :error="fieldError('address')" />
          </div>
          <div class="col-12 col-md-6 mb-3">
            <f-text
              id="bio_phone"
              label="Phone Number"
              v-model="form.phone"
              required
              :error="fieldError('phone')"
              hint="Please enter the phone in a similar format +614XXXXXXXXX without spaces"
              placeholder="+614XXXXXXXXX"
            />
          </div>
          <div class="col-12 col-md-6 mb-3">
            <f-text id="bio_email" label="Email" v-model="form.email" type="email" required :error="fieldError('email')" />
          </div>
          <div class="col-12 col-md-6 mb-3">
            <f-text id="bio_property" label="Property of origin" v-model="form.propertyOfOrigin" required :error="fieldError('property_of_origin')" />
          </div>
          <div class="col-12 col-md-6 mb-3">
            <f-text id="bio_destination" label="Destination" v-model="form.destination" required :error="fieldError('destination')" />
          </div>
          <div class="col-12 col-md-6 mb-3">
            <f-text id="bio_receiver" label="Name of person receiving horses" v-model="form.namePersonReceiving" :error="fieldError('name_person_receiving')" />
          </div>
          <div class="col-12 col-md-6 mb-3">
            <f-text id="bio_pic" label="PIC Number" v-model="form.pic" required :error="fieldError('pic')" />
          </div>
        </div>

        <div class="mb-3">
          <f-radio-group
            name="movementType"
            label="What type of movement is this?"
            v-model="form.movementType"
            :options="MOCK_MOVEMENT_TYPES"
            required
            :error="fieldError('movement_type')"
          />
        </div>

        <div class="mb-3">
          <f-textarea
            id="bio_ticks"
            label="How did you ensure horse/s are tick free?"
            v-model="form.reasonNoTicks"
            :rows="3"
            hint="If This Movement is From Cattle Tick Infected Zone Into Cattle Tick Free Zone, please answer this question"
          />
        </div>

        <div class="mb-3">
          <h5>Horses</h5>
          <div class="table-responsive">
            <table class="table table-bordered biosecurity-horses-table">
              <thead>
                <tr>
                  <th style="width: 36px">#</th>
                  <th>Registered Name</th>
                  <th>Breed</th>
                  <th>Sex</th>
                  <th>Identification marks</th>
                  <th>PIC Number</th>
                  <th style="width: 36px"></th>
                </tr>
              </thead>
              <tbody>
                <biosecurity-horse-row
                  v-for="(horse, idx) in form.horses"
                  :key="horse._key"
                  :index="idx"
                  v-model="form.horses[idx]"
                  :removable="idx > 0"
                  :errors="errors"
                  @remove="removeHorse(idx)"
                />
              </tbody>
            </table>
          </div>
          <div class="d-flex justify-content-end">
            <app-button variant="primary" size="sm" :block="false" @click="addHorse">
              Add One More Horse
            </app-button>
          </div>
        </div>

        <div class="mb-3 text-muted small">
          <p>I declare that the horse/s named above has / have been in good health, eating normally and not shown signs of illness during the last five days leading up to this event. I give my authorisation for the Event Organising Committee/Biosecurity Manager to call for veterinary inspection of the horse/s named above and in my care should they be showing signs of illness at any time during the course of the event. I agree to pay any veterinary fees incurred for the abovementioned horses as a result of this veterinary examination. I agree that should my horse at any time become sick at the event I will advise the event organisers immediately.</p>
          <p>I AGREE TO ENSURE THAT: 1. All horses, vehicles and equipment accompanying horse/s will be clean and free of solid material (that could contain disease agents) prior to departing property of origin. I FURTHER DECLARE THAT: 2. The information contained in this Biosecurity Declaration is true and correct to the best of my knowledge. 3 I agree to abide and those people that are assisting me on the day, by all conditions that may be imposed at any time by the Event Organising Committee/Biosecurity Manager. 4. I acknowledge that in failure to comply, or those people that are assisting me on the day fail to comply, may be directed to leave the event and my nominations will be forfeited. 5. I acknowledge that decontamination and disinfection procedures may be required of me if instructed by the Event Organising Committee/Biosecurity Manager. 6. I acknowledge that there is a possibility that horses might become infected with disease agents as a result of any movements and if necessary horses and premises will be quarantined in accordance with any legislation covering such occurrences including policies and procedures in effect at that time. I agree and acknowledge that the Biosecurity Manager/Event Organising Committee, its State or National Affiliated bodies and their members are not in any way liable for any cost, expense, loss, damage, claim, action, proceeding or other liability incurred by or made against me as a result of any movement of horses to the event/farm.</p>
        </div>

        <div class="row mb-3">
          <div class="col-12 col-md-6 mb-3 mb-md-0">
            <f-text id="bio_signature" label="Name" v-model="form.signatureName" required :error="fieldError('name')" />
          </div>
          <div class="col-12 col-md-6 mb-3 mb-md-0">
            <f-text id="bio_date" label="Date" v-model="form.date" type="date" required :error="fieldError('date')" />
          </div>
          <div class="col-12">
            <f-checkbox id="bio_terms" label="I agree to terms" v-model="form.terms" :font-normal="true" required :error="fieldError('terms')" />
          </div>
        </div>

        <app-button variant="primary" size="sm" :block="false" :loading="submitting" @click="onSubmit">Submit</app-button>
      </form>
    </div>
  </tab-layout>
</template>

<script>
import { defineComponent, ref, computed, onMounted, getCurrentInstance } from '@vue/composition-api'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import AppButton from '@/components/AppButton'
import FText from '@/components/FormFields/FText'
import FTextarea from '@/components/FormFields/FTextarea'
import FRadioGroup from '@/components/FormFields/FRadioGroup'
import FCheckbox from '@/components/FormFields/FCheckbox'
import BiosecurityHorseRow from '@/components/Biosecurity/BiosecurityHorseRow'
import useBiosecurityForm from '@/composables/biosecurity/useBiosecurityForm'

export default defineComponent({
  name: 'BiosecurityForm',
  components: {
    TabLayout,
    PageHeading,
    AppButton,
    FText,
    FTextarea,
    FRadioGroup,
    FCheckbox,
    BiosecurityHorseRow
  },
  setup() {
    const vm = getCurrentInstance()
    const draftId = computed(() => vm.proxy.$route.params.draftId)
    let horseKey = 2

    const MOCK_MOVEMENT_TYPES = ref([
      { value: 1, label: 'Movement From Cattle Tick Free Zone Into Cattle Tick Infected Zone' },
      { value: 2, label: 'Movement Within Cattle Tick Free Zone' },
      { value: 3, label: 'Movement From Cattle Tick Infected Zone Into Cattle Tick Free Zone' },
      { value: 4, label: 'Movement Within Cattle Tick Infected Zone' }
    ])

    const { isEdit, loading, submitting, form, errors, fetchForm, submitForm, downloadBlankForm } = useBiosecurityForm(draftId.value)

    onMounted(() => {
      if (draftId.value) fetchForm()
    })

    function addHorse() {
      form.horses.push({ _key: horseKey++, registeredName: '', breed: '', sex: '', identificationMarks: '', pic: '' })
    }

    function removeHorse(idx) {
      form.horses.splice(idx, 1)
    }

    function fieldError(key) {
      const errs = errors.value[key]
      return errs && errs.length > 0 ? errs[0] : null
    }

    async function onSubmit() {
      const success = await submitForm()
      if (success) {
        const from = vm.proxy.$route.query.from
        if (from) {
          vm.proxy.$router.push(from)
        } else {
          vm.proxy.$router.back()
        }
      }
    }

    return {
      isEdit,
      loading,
      submitting,
      form,
      errors,
      MOCK_MOVEMENT_TYPES,
      addHorse,
      removeHorse,
      onSubmit,
      downloadBlankForm,
      fieldError
    }
  }
})
</script>

<style scoped>
.biosecurity-form {
  max-width: 66.666%;
}

.biosecurity-horses-table th,
.biosecurity-horses-table >>> td {
  font-size: 1rem;
  vertical-align: middle;
}

.biosecurity-horses-table >>> .form-control {
  font-size: 1rem;
  padding: 0.375rem 0.75rem;
  height: auto;
}

@media (max-width: 768px) {
  .biosecurity-form {
    max-width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Biosecurity/BiosecurityForm.vue